// In-memory Claude-Artifacts-style panel state. Unlike debugMode/sidebarLayout
// this is deliberately NOT persisted to storage — artifact revisions are
// per-conversation scratch state, not a durable user preference.
//
// Keyed by threadId (mirrors the `locks: Record<threadId, ThreadLockState>`
// convention in shared/chat-workspace/lockStore.ts) so switching threads or
// agents never leaks one thread's artifact history/open-state into another —
// each thread gets its own isolated panel slice, and a thread that has never
// called `update_artifact` simply has no entry (closed/empty by default).

import { create } from 'zustand';

export interface ArtifactRevision {
  title: string;
  html: string;
  timestamp: number;
}

export interface ThreadArtifactPanel {
  isOpen: boolean;
  title: string;
  html: string;
  revisions: ArtifactRevision[];
  currentRevisionIndex: number;
}

const EMPTY_PANEL: ThreadArtifactPanel = {
  isOpen: false,
  title: '',
  html: '',
  revisions: [],
  currentRevisionIndex: -1,
};

interface ArtifactPanelState {
  panels: Record<string, ThreadArtifactPanel>;
  /** Appends a new revision (a completed `update_artifact` tool call), opening the panel if closed. */
  pushRevision: (threadId: string, title: string, html: string) => void;
  /**
   * Updates the most recent revision in place instead of appending — used
   * while a single tool call's `html` arg is still streaming in, so the
   * preview grows live without flooding the revision history with one entry
   * per partial chunk.
   */
  updateLatestRevision: (threadId: string, title: string, html: string) => void;
  /** Steps to a past/future revision without discarding history. */
  viewRevision: (threadId: string, index: number) => void;
  close: (threadId: string) => void;
  /**
   * Manually reopens the panel for a thread (the on-demand "reopen" affordance) —
   * shows whatever revision is already in the store, if any. Never fabricates
   * data: a thread with no revisions yet just reopens to an empty panel, which
   * ArtifactPanel renders as a placeholder state instead of a blank iframe.
   */
  open: (threadId: string) => void;
}

function withPanel(
  panels: Record<string, ThreadArtifactPanel>,
  threadId: string,
  update: (panel: ThreadArtifactPanel) => ThreadArtifactPanel,
): Record<string, ThreadArtifactPanel> {
  return { ...panels, [threadId]: update(panels[threadId] ?? EMPTY_PANEL) };
}

export const useArtifactPanelStore = create<ArtifactPanelState>((set) => ({
  panels: {},
  pushRevision: (threadId, title, html) =>
    set((state) => ({
      panels: withPanel(state.panels, threadId, (panel) => {
        const revisions = [...panel.revisions, { title, html, timestamp: Date.now() }];
        return { ...panel, revisions, currentRevisionIndex: revisions.length - 1, title, html, isOpen: true };
      }),
    })),
  updateLatestRevision: (threadId, title, html) =>
    set((state) => ({
      panels: withPanel(state.panels, threadId, (panel) => {
        if (panel.currentRevisionIndex < 0) {
          const revisions = [{ title, html, timestamp: Date.now() }];
          return { ...panel, revisions, currentRevisionIndex: 0, title, html, isOpen: true };
        }
        const revisions = [...panel.revisions];
        revisions[panel.currentRevisionIndex] = { ...revisions[panel.currentRevisionIndex], title, html };
        return { ...panel, revisions, title, html };
      }),
    })),
  viewRevision: (threadId, index) =>
    set((state) => {
      const panel = state.panels[threadId];
      const revision = panel?.revisions[index];
      if (!revision) return state;
      return {
        panels: withPanel(state.panels, threadId, (p) => ({
          ...p,
          currentRevisionIndex: index,
          title: revision.title,
          html: revision.html,
        })),
      };
    }),
  close: (threadId) =>
    set((state) => ({ panels: withPanel(state.panels, threadId, (panel) => ({ ...panel, isOpen: false })) })),
  open: (threadId) =>
    set((state) => ({ panels: withPanel(state.panels, threadId, (panel) => ({ ...panel, isOpen: true })) })),
}));

export function useThreadArtifactPanel(threadId: string): ThreadArtifactPanel {
  return useArtifactPanelStore((s) => s.panels[threadId] ?? EMPTY_PANEL);
}
